import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type React from 'react'
import {
  AbsoluteFill,
  useCurrentFrame,
  useDelayRender,
  useVideoConfig,
} from 'remotion'
import { FallbackImg } from '../../components/FallbackImg'
import ZealousSvg from '../../assets/ZealousSvg'
import { BarsVisualization } from '../../components/waveform/BarsVisualization'
import { HillsVisualization } from '../../components/waveform/HillsVisualization'
import { RadialBarsVisualization } from '../../components/waveform/RadialBarsVisualization'
import { WaveVisualization } from '../../components/waveform/WaveVisualization'
import { MediaTimelineContext } from '../../utils/mediaTimeline'
import { useAssets, useFollowedScenes, useTheme } from '../contexts'
import { useLoadFont } from '../fonts'
import type { ComponentItem } from '../types'
import { speakerInitials, type CurrentSpeaker } from '../captionWords'
import {
  INACTIVE_SPEAKER_OPACITY,
  useCurrentSpeaker,
} from '../useCurrentSpeaker'
import { fitBox } from '../refit'
import { pictureRadius } from './pictureRadius'
import { assetAt, visualizerPieces } from './visualizerPieces'
import { visualizerGeometry } from './visualizerGeometry'
import {
  COMPONENT_DEFAULTS,
  COMPONENT_IDS,
  COMPONENT_NAMES,
  COMPONENT_PROPS,
  type ComponentId,
} from './componentProps'

/**
 * The built-in components (#77): a title card, a logo outro, an audio
 * visualizer and a picture (a fixed one, or the current speaker's avatar).
 * A speaker's name or handle is a dynamic TEXT item, not a component. Each
 * reads colours and fonts from the org theme,
 * so a brand kit (#493) later changes what they look like without touching
 * a single render's config. `componentProps.ts` owns their props schemas;
 * `registry.test.ts` keeps the two maps aligned.
 */

type ItemOf<K extends ComponentId> = Extract<ComponentItem, { componentId: K }>

function useThemeFont() {
  const theme = useTheme()
  const loaded = useLoadFont({
    fontFamily: theme.fonts.primary,
    fontVariant: 'normal',
    fontWeight: '700',
  })
  return { theme, loaded }
}

function TitleCard({ item }: { item: ItemOf<'title-card'> }) {
  const { theme, loaded } = useThemeFont()
  if (!loaded) return null
  const size = Math.min(item.width, item.height)
  return (
    <AbsoluteFill
      style={{
        backgroundColor: theme.colors.primary,
        color: theme.colors.secondary,
        fontFamily: theme.fonts.primary,
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: size * 0.08,
      }}
    >
      <div style={{ fontSize: size * 0.09, fontWeight: 700, lineHeight: 1.15 }}>
        {item.props.title}
      </div>
      {item.props.subtitle ? (
        <div
          style={{
            fontSize: size * 0.045,
            fontWeight: 500,
            marginTop: size * 0.03,
            color: theme.colors.accent,
          }}
        >
          {item.props.subtitle}
        </div>
      ) : null}
    </AbsoluteFill>
  )
}

function LogoOutro({ item }: { item: ItemOf<'logo-outro'> }) {
  const { theme, loaded } = useThemeFont()
  if (!loaded) return null
  const size = Math.min(item.width, item.height)
  return (
    <AbsoluteFill
      style={{
        backgroundColor: theme.colors.primary,
        color: theme.colors.secondary,
        fontFamily: theme.fonts.primary,
        justifyContent: 'center',
        alignItems: 'center',
        gap: size * 0.05,
      }}
    >
      <ZealousSvg
        color={theme.colors.secondary}
        style={{ height: size * 0.14 }}
      />
      {item.props.text ? (
        <div style={{ fontSize: size * 0.05, fontWeight: 500, opacity: 0.8 }}>
          {item.props.text}
        </div>
      ) : null}
    </AbsoluteFill>
  )
}

/**
 * Reads the sound of its scene (#541): analyzes the audio rendition of the
 * bound item against its `waveform.dat` peak, through the same windowed FFT
 * the clip layouts use, and maps frames through the scene's pieces so cuts
 * are followed (`utils/mediaTimeline.ts`). Outside the scene it draws
 * nothing. An uploaded file is a scene too, for the visualizer.
 */
function AudioVisualizer({ item }: { item: ItemOf<'audio-visualizer'> }) {
  const theme = useTheme()
  const assets = useAssets()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const mediaItems = useFollowedScenes(item.zealous?.sceneId, item.forTrackId)

  const pieces = useMemo(
    () => visualizerPieces({ mediaItems, from: item.from, fps, assets }),
    [mediaItems, item.from, fps, assets],
  )

  const assetId = assetAt(pieces, frame)
  const asset = assetId ? assets[assetId] : undefined
  const media =
    asset && asset.type !== 'transcript' && asset.type !== 'image'
      ? asset
      : null

  if (!media?.remoteUrl) return null
  const color = item.props.color ?? theme.colors.accent
  const common = {
    mediaUrl: media.audioUrl ?? media.remoteUrl,
    waveformUrl: media.waveformUrl,
    color,
  }
  // The numbers behind the item's named look (#546).
  const geometry = visualizerGeometry(
    item.props.variant,
    item.width,
    item.height,
  )

  return (
    <MediaTimelineContext.Provider value={pieces}>
      <AbsoluteFill style={{ justifyContent: 'center', alignItems: 'center' }}>
        {item.props.variant === 'radial' ? (
          // A ring, not a disc: the bars start well out from the centre, so
          // a picture placed in the middle leaves them visible.
          <RadialBarsVisualization
            {...common}
            diameter={Math.min(item.width, item.height)}
            innerRadius={geometry.innerRadius}
            lineThickness={geometry.lineThickness}
            gapSize={geometry.gapSize}
            roundness={geometry.roundness}
          />
        ) : item.props.variant === 'hills' ? (
          <HillsVisualization
            {...common}
            fillColor={color}
            height={item.height}
            width={item.width}
          />
        ) : item.props.variant === 'wave' ? (
          <WaveVisualization
            {...common}
            height={item.height}
            width={item.width}
          />
        ) : (
          <BarsVisualization
            {...common}
            width={item.width}
            height={item.height}
            gapSize={geometry.gapSize}
            lineThickness={geometry.lineThickness}
            placement={geometry.placement}
            roundness={geometry.roundness}
            mirror={geometry.mirror}
          />
        )}
      </AbsoluteFill>
    </MediaTimelineContext.Provider>
  )
}

/**
 * A picture from a URL, fitted in its box (#77): what the v1 layouts drew
 * for the host avatar and the logo badge, which v2 had no item for. The URL
 * is whatever the blueprint was given — the host's X avatar, the org logo —
 * and `<Img>` makes Lambda wait for it. A speaker picture shows the avatar
 * of whoever is talking in its scene, like a dynamic text. Pinned to a
 * participant (#566), it shows that person throughout: its ring (`border*`)
 * is drawn only while they are the active speaker, it dims while someone
 * else talks. A speaker with no avatar, pinned or not, is a disc with their
 * initials, as the app draws an avatar it has no picture for.
 */
function Picture({ item }: { item: ItemOf<'picture'> }) {
  const { fit, borderColor, borderWidth } = item.props
  const isSpeaker = item.props.source === 'speaker'
  const pin = isSpeaker ? item.zealous?.participantId : undefined
  const { speaker, active } = useCurrentSpeaker(
    isSpeaker ? item.zealous?.sceneId : undefined,
    isSpeaker ? item.forTrackId : undefined,
    item.from,
    pin,
  )
  const url = isSpeaker ? speaker?.avatarUrl : item.props.url
  const natural = useNaturalSize(fit === 'contain' ? url : undefined)
  const frame = {
    borderRadius: pictureRadius(item.props, item.width, item.height),
    boxSizing: 'border-box' as const,
    border:
      borderColor && borderWidth && active
        ? `${borderWidth}px solid ${borderColor}`
        : undefined,
  }
  const slot = { opacity: active ? 1 : INACTIVE_SPEAKER_OPACITY }
  if (!url) {
    if (!speaker) return null
    return (
      <SpeakerInitials
        speaker={speaker}
        size={Math.min(item.width, item.height)}
        style={{ ...frame, ...slot }}
      />
    )
  }
  // Fitted inside, the picture is letterboxed: the corners and the border
  // belong to the picture itself, not to the empty box around it, so it is
  // drawn at its fitted size once its natural size is known.
  if (fit === 'contain' && natural) {
    const box = fitBox(natural, { width: item.width, height: item.height })
    return (
      <AbsoluteFill style={slot}>
        <FallbackImg
          src={url}
          style={{
            ...frame,
            position: 'absolute',
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            objectFit: 'cover',
          }}
        />
      </AbsoluteFill>
    )
  }
  return (
    <AbsoluteFill style={{ ...frame, ...slot, overflow: 'hidden' }}>
      <FallbackImg
        src={url}
        style={{ width: '100%', height: '100%', objectFit: fit }}
      />
    </AbsoluteFill>
  )
}

/**
 * The disc a speaker picture draws when its person has no avatar (#566): the
 * theme's secondary colour with their initials in the primary, in the theme
 * font. Its own component so that only this branch waits on the theme font:
 * a url picture must never depend on it.
 */
function SpeakerInitials({
  speaker,
  size,
  style,
}: {
  speaker: Pick<CurrentSpeaker, 'name' | 'handle'>
  size: number
  style: React.CSSProperties
}) {
  const { theme, loaded } = useThemeFont()
  if (!loaded) return null
  return (
    <AbsoluteFill
      style={{
        ...style,
        backgroundColor: theme.colors.secondary,
        color: theme.colors.primary,
        fontFamily: theme.fonts.primary,
        fontWeight: 700,
        fontSize: size * 0.4,
        lineHeight: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {speakerInitials(speaker)}
    </AbsoluteFill>
  )
}

/**
 * An image's natural size, for drawing a fitted-inside picture at its real
 * shape. The frame waits for it (`delayRender`), so a render never captures
 * the box before the picture is measured; a failed load releases the frame
 * and the picture falls back to the plain box.
 */
function useNaturalSize(
  url: string | null | undefined,
): { width: number; height: number } | null {
  const { delayRender, continueRender } = useDelayRender()
  const [size, setSize] = useState<{
    url: string
    width: number
    height: number
  } | null>(null)
  const pending = useRef<number | null>(null)
  const release = useCallback(() => {
    if (pending.current === null) return
    continueRender(pending.current)
    pending.current = null
  }, [continueRender])
  useEffect(() => {
    if (!url) return
    pending.current = delayRender(`Measuring picture ${url}`)
    const image = new Image()
    image.onload = () =>
      setSize({ url, width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = release
    image.src = url
    return () => {
      image.onload = null
      image.onerror = null
      release()
    }
  }, [url, delayRender, release])
  // Released once the measured size has rendered, not when it arrives.
  useEffect(() => {
    if (size && size.url === url) release()
  }, [size, url, release])
  return size && size.url === url && size.width > 0 && size.height > 0
    ? size
    : null
}

export const COMPONENTS: {
  [K in ComponentId]: {
    id: K
    name: string
    propsSchema: (typeof COMPONENT_PROPS)[K]
    defaultProps: (typeof COMPONENT_DEFAULTS)[K]
    Component: React.FC<{ item: ItemOf<K> }>
  }
} = {
  'title-card': {
    id: 'title-card',
    name: COMPONENT_NAMES['title-card'],
    propsSchema: COMPONENT_PROPS['title-card'],
    defaultProps: COMPONENT_DEFAULTS['title-card'],
    Component: TitleCard,
  },
  'logo-outro': {
    id: 'logo-outro',
    name: COMPONENT_NAMES['logo-outro'],
    propsSchema: COMPONENT_PROPS['logo-outro'],
    defaultProps: COMPONENT_DEFAULTS['logo-outro'],
    Component: LogoOutro,
  },
  'audio-visualizer': {
    id: 'audio-visualizer',
    name: COMPONENT_NAMES['audio-visualizer'],
    propsSchema: COMPONENT_PROPS['audio-visualizer'],
    defaultProps: COMPONENT_DEFAULTS['audio-visualizer'],
    Component: AudioVisualizer,
  },
  picture: {
    id: 'picture',
    name: COMPONENT_NAMES.picture,
    propsSchema: COMPONENT_PROPS.picture,
    defaultProps: COMPONENT_DEFAULTS.picture,
    Component: Picture,
  },
}

export { COMPONENT_IDS }
